import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import U2000BasicDialog from "../../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000ComboBoxBase from "../../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase";
import { FaUndoAlt, FaPlus } from "react-icons/fa";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../../../u2000Core/axios/U2000HandleApiError";
import {
  U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG,
  U3141_DOCUMENT_UPLOAD_MAX_FILES,
  U3141_SINGLE_SLOT_DOCUMENT_TYPES,
  luHandleAppiError,
  u3141IsSingleSlotDocumentType,
  type U3141MasterLotChain,
  type U3141LotDetailDocument,
} from "../../../U3141Service";
import { U3141DocumentPdfFilesQueueUpload } from "./U3141DocumentPdfFilesQueueUpload";
import { useU3141AddDocumentHandlers } from "./U3141UseDocumentsHandlers";
import { u3141ValidateDocumentUploadFile } from "../../../tools/u3141DocumentUploadFileQueue";
import {
  u3141AddDocumentDialogAllowsMultipleFiles,
  u3141DocumentCreateSuccessMessageKey,
  u3141LotHasDocumentOfType,
  u3141ResolveAddDocumentTypeOptions,
} from "../../../tools/u3141DocumentAddDialogTypes";
import { NS } from "../../../U3141";

/** Classes des boutons alignées sur U2000SaveCancelDialog (formulaire). */
const cancelButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
  active:bg-gray-100 active:scale-95
  disabled:cursor-not-allowed disabled:opacity-60`;
const primaryButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg shadow-xs transition-all duration-200
  hover:bg-green-700 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2
  active:bg-green-800 active:scale-95
  disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60 disabled:shadow-none`;

export interface U3141AddDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  masterLotChainId: string;
  childLotChainId: string;
  /** Projet courant (affiché en lecture seule). */
  currentProject: U3141MasterLotChain | null;
  /** Documents déjà présents pour ce lot (filtrage combobox + aide remplacement index BL-0708). */
  existingDocuments: U3141LotDetailDocument[];
  /** Types autorisés par initLU (repli sur `U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG` si absent). */
  addableDocumentTypes?: string[];
  /** Types à emplacement unique (initLU `singleSlotDocumentTypes`, BL-0708). */
  singleSlotDocumentTypes?: string[];
  /** BL-0712 — type pré-sélectionné (menu graphe) : masque le combobox type. */
  initialDocumentType?: string | null;
  overlayClassName?: string;
}

type DocumentTypeOption = { key: string; description: string };

export const U3141AddDocumentDialog: React.FC<U3141AddDocumentDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  masterLotChainId,
  childLotChainId,
  currentProject,
  existingDocuments = [],
  addableDocumentTypes,
  singleSlotDocumentTypes = [...U3141_SINGLE_SLOT_DOCUMENT_TYPES],
  initialDocumentType = null,
  overlayClassName,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  /** Incrémenté à chaque ouverture pour forcer le remount du champ fichier (input natif vidé). */
  const [formResetKey, setFormResetKey] = useState(0);

  const { createDocument } = useU3141AddDocumentHandlers({
    masterLotChainId,
    childLotChainId,
  });

  const allowedAddableTypes = useMemo(
    () => (addableDocumentTypes?.length ? addableDocumentTypes : [...U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG]),
    [addableDocumentTypes],
  );

  const resolvedTypeKeys = useMemo(
    () => u3141ResolveAddDocumentTypeOptions(allowedAddableTypes, existingDocuments, singleSlotDocumentTypes),
    [allowedAddableTypes, existingDocuments, singleSlotDocumentTypes],
  );

  const documentTypeOptions: DocumentTypeOption[] = useMemo(
    () =>
      resolvedTypeKeys.map((type) => ({
        key: type,
        description: t(`lblDocumentType.${type}`, { ns: NS }) || type,
      })),
    [t, resolvedTypeKeys],
  );

  const allTypesPresent = documentTypeOptions.length === 0;

  const allowsMultipleFiles = u3141AddDocumentDialogAllowsMultipleFiles(documentType);
  const uploadMaxFiles = allowsMultipleFiles ? U3141_DOCUMENT_UPLOAD_MAX_FILES : 1;

  const showIndexReplaceHint =
    !!documentType &&
    u3141IsSingleSlotDocumentType(documentType, singleSlotDocumentTypes) &&
    u3141LotHasDocumentOfType(existingDocuments, documentType);

  const selectedTypeOption = useMemo(
    () => (documentType ? documentTypeOptions.find((o) => o.key === documentType) ?? null : null),
    [documentType, documentTypeOptions],
  );

  const presetDocumentType =
    initialDocumentType && documentTypeOptions.some((o) => o.key === initialDocumentType) ? initialDocumentType : null;
  const lockDocumentType = Boolean(presetDocumentType);

  useEffect(() => {
    if (isOpen) {
      const nextType =
        initialDocumentType && documentTypeOptions.some((o) => o.key === initialDocumentType)
          ? initialDocumentType
          : null;
      setDocumentType(nextType);
      setFiles([]);
      setSubmitting(false);
      setFormResetKey((k) => k + 1);
    }
  }, [isOpen, initialDocumentType, documentTypeOptions]);

  useEffect(() => {
    if (documentType && !documentTypeOptions.some((o) => o.key === documentType)) {
      setDocumentType(null);
    }
  }, [documentType, documentTypeOptions]);

  useEffect(() => {
    if (!allowsMultipleFiles && files.length > 1) {
      setFiles((prev) => prev.slice(0, 1));
    }
  }, [allowsMultipleFiles, files.length]);

  const handleFilesChange = useCallback((next: File[]) => {
    setFiles(next);
  }, []);

  const handleSubmit = async () => {
    if (!documentType || files.length === 0) return;

    for (const file of files) {
      const validationKey = u3141ValidateDocumentUploadFile(file);
      if (validationKey) {
        toast.error(t(validationKey, { ns: NS }));
        return;
      }
    }

    const hadExistingSameType = u3141LotHasDocumentOfType(existingDocuments, documentType);

    setSubmitting(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("documentType", documentType);
        formData.append("file", file);
        if (currentProject?.projectNumber) {
          formData.append("projectNumber", currentProject.projectNumber);
        }
        await createDocument(formData);
      }
      const messageKey = u3141DocumentCreateSuccessMessageKey({
        documentType,
        hadExistingSameType,
        fileCount: files.length,
        singleSlotDocumentTypes,
      });
      if (messageKey === "msgDocumentsAddedCount") {
        toast.success(t(messageKey, { ns: NS, count: files.length }));
      } else {
        toast.success(t(messageKey, { ns: NS }));
      }
      setDocumentType(null);
      setFiles([]);
      onSuccess();
      onClose();
    } catch (error: unknown) {
      setSubmitting(false);
      const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
    }
  };

  const isValid = !allTypesPresent && !!documentType && files.length > 0;

  if (!isOpen) return null;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={() => onClose()}
      title={t("dlgAddDocumentTitle", { ns: NS })}
      width="x-large"
      height="max-h-[90vh]"
      contentOverflowVisible
      overlayClassName={overlayClassName}
    >
      <div className="flex flex-col h-full focus:outline-hidden">
        <section className="flex-1 px-6 py-4 overflow-visible min-h-0">
          <div className="flex flex-col gap-4">
            <U3141DocumentPdfFilesQueueUpload
              key={formResetKey}
              label={t("lblDocumentFile", { ns: NS })}
              files={files}
              onChange={handleFilesChange}
              maxFiles={uploadMaxFiles}
              allowsMultipleFiles={allowsMultipleFiles}
              disabled={submitting}
              formResetKey={formResetKey}
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">{t("lblDocumentType", { ns: NS })}</label>
              {files.length > 0 && !documentType ? (
                <p className="mb-2 text-xs text-amber-800">{t("msgDocumentUploadSelectTypeToSubmit", { ns: NS })}</p>
              ) : null}
              {showIndexReplaceHint ? (
                <p className="mb-2 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                  {t("msgIndexUploadWillReplace", { ns: NS })}
                </p>
              ) : null}
              {allTypesPresent ? (
                <p className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {t("msgAllDocumentTypesPresent", { ns: NS })}
                </p>
              ) : lockDocumentType && presetDocumentType ? (
                <p className="rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900">
                  {t(`lblDocumentType.${presetDocumentType}`, { ns: NS }) || presetDocumentType}
                </p>
              ) : (
                <U2000ComboBoxBase<DocumentTypeOption>
                  keyField="key"
                  descField="description"
                  data={documentTypeOptions}
                  value={selectedTypeOption}
                  onChangeObject={(o) => setDocumentType(o?.key ?? null)}
                  placeHolder={t("lblDocumentTypePlaceholder", { ns: NS })}
                  noneItemLabel={t("lblNoneSelection", { ns: NS })}
                />
              )}
            </div>
          </div>
        </section>

        <footer className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl shrink-0">
          <button
            type="button"
            className={primaryButtonClass}
            onClick={() => void handleSubmit()}
            disabled={!isValid || submitting}
            aria-label={t("dlgLblAdd", { ns: NS })}
          >
            <FaPlus className="w-4 h-4" />
            <span>{t("dlgLblAdd", { ns: NS })}</span>
          </button>
          <button
            type="button"
            className={cancelButtonClass}
            onClick={() => onClose()}
            disabled={submitting}
            aria-label={t("dlgLblCancel", { ns: NS })}
          >
            <FaUndoAlt className="w-4 h-4" />
            <span>{t("dlgLblCancel", { ns: NS })}</span>
          </button>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
